import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import CrossIcon from "@atlaskit/icon/core/cross";
import {token} from "@atlaskit/tokens";
import {useNavigate} from "@tanstack/react-router";
import type {ReactNode} from "react";
import {AnankeHeading} from "@vesta/ananke/components/anankeHeading";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import type {EventContainerAddEventItem} from "~/components/eventContainerAddEventButton";
import {StandardModalHeaderDivider} from "~/components/standardModalHeader";

function CreateOptionRowButton({label, onClick}: {label: string; onClick: () => void}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: token("space.150", "12px"),
                height: "40px",
                minHeight: "32px",
                padding: `0 ${token("space.150", "12px")}`,
                borderRadius: "4px",
                background: token("color.background.input"),
                color: token("color.text"),
                cursor: "pointer",
            }}
        >
            <AnankeText color="color.text.subtle">{label}</AnankeText>
            <ChevronRightIcon label="" color={token("color.icon.subtle")} />
        </button>
    );
}

/** Create shortcuts list, shared by the desktop side panel and the mobile bottom sheet. */
export function JourneyProfileCreateOptionRows({
    items,
    onNavigate,
}: {
    items: ReadonlyArray<EventContainerAddEventItem>;
    /** Called before navigating so the host can dismiss its surface. */
    onNavigate?: () => void;
}): ReactNode {
    const navigate = useNavigate();

    if (items.length === 0) {
        return <AnankeText color="color.text.subtlest">No create actions available.</AnankeText>;
    }

    return (
        <div className="grid gap-ds-space-100" style={{fontFamily: "Inter"}}>
            {items.map((item) => (
                <CreateOptionRowButton
                    key={item.label}
                    label={item.label}
                    onClick={() => {
                        onNavigate?.();
                        void navigate({to: item.href});
                    }}
                />
            ))}
        </div>
    );
}

/**
 * Create-options side panel for the journey profile (desktop aside or page portal).
 * Same shortcuts as the mobile bottom sheet; separate shell so it can replace the
 * Lifecycle profile aside without stacking.
 */
export function JourneyProfileAsideCreateSurface({
    items,
    onClose,
}: {
    items: ReadonlyArray<EventContainerAddEventItem>;
    onClose: () => void;
}): ReactNode {
    return (
        <div className="grid h-full min-h-0 min-w-0 w-full grid-rows-[auto_auto_minmax(0,1fr)] overflow-hidden bg-ds-elevation-surface-current">
            <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_max-content] items-center gap-ds-space-200 px-ds-space-250 py-ds-space-150">
                <AnankeHeading size="small">Create</AnankeHeading>
                <AnankeIconButton
                    type="button"
                    appearance="subtle"
                    icon={CrossIcon}
                    label="Close"
                    onClick={onClose}
                />
            </div>
            <StandardModalHeaderDivider />
            <div className="relative min-h-0 min-w-0 overflow-hidden">
                <div className="absolute inset-0 min-h-0 min-w-0 overflow-y-auto px-ds-space-250 py-ds-space-200">
                    <JourneyProfileCreateOptionRows items={items} onNavigate={onClose} />
                </div>
            </div>
        </div>
    );
}
